import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteTier,
  listTiers,
  updateTier,
  upsertTier,
} from "../api/weddings";
import {
  TicketTier,
  TicketTierRow,
  TICKET_TIER_DESCRIPTIONS,
  TICKET_TIER_LABELS,
} from "../types";
import { formatPrice } from "../utils/format";
import { tierTracksCapacity } from "../utils/tiers";
import Loading from "./Loading";
import ErrorMessage from "./ErrorMessage";

interface Props {
  weddingId: string;
}

const TIER_TYPES = Object.keys(TICKET_TIER_LABELS) as TicketTier[];

const emptyForm = {
  tier_type: "party_not_presents" as TicketTier,
  label: TICKET_TIER_LABELS.party_not_presents,
  price_usd: "",
  capacity: "10",
  close_date: "",
  is_active: true,
  editingId: null as string | null,
};

const toDateInput = (value?: string | null) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.slice(0, 10);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const capacityLabel = (tier: TicketTierRow) => {
  if (!tierTracksCapacity(tier.tier_type)) {
    return `${tier.sold_count} / N/A`;
  }
  return `${tier.sold_count} / ${tier.capacity}`;
};

const TierSetup: React.FC<Props> = ({ weddingId }) => {
  const [tiers, setTiers] = useState<TicketTierRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(() => ({ ...emptyForm }));
  const [editNonce, setEditNonce] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const priceRef = useRef<HTMLInputElement>(null);

  const tracksCapacity = tierTracksCapacity(form.tier_type);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await listTiers(weddingId);
      setTiers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load tiers");
    } finally {
      setLoading(false);
    }
  }, [weddingId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!form.editingId || !formRef.current) return;
    formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    const focusable = formRef.current.querySelector<HTMLElement>(
      "input:not([disabled]):not([readonly]):not([type=hidden]), select:not([disabled]), textarea:not([disabled])"
    );
    (focusable || priceRef.current)?.focus();
  }, [form.editingId, editNonce]);

  const resetForm = () => setForm({ ...emptyForm });

  const startEdit = (tier: TicketTierRow) => {
    setForm({
      tier_type: tier.tier_type,
      label: tier.label || TICKET_TIER_LABELS[tier.tier_type],
      price_usd: (tier.price_cents / 100).toFixed(2),
      capacity: String(tier.capacity ?? 0),
      close_date: toDateInput(tier.close_date),
      is_active: tier.is_active,
      editingId: tier.id,
    });
    setEditNonce((n) => n + 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const parsedPrice = parseFloat(form.price_usd);
    const priceCents = Number.isFinite(parsedPrice) ? Math.round(parsedPrice * 100) : 0;
    const parsedCapacity = parseInt(form.capacity || "0", 10);
    const capacity = tierTracksCapacity(form.tier_type)
      ? (Number.isFinite(parsedCapacity) ? parsedCapacity : 0)
      : 0;

    try {
      if (form.editingId) {
        await updateTier(weddingId, form.editingId, {
          price_cents: priceCents,
          capacity,
          close_date: form.close_date || null,
          is_active: form.is_active,
        });
      } else {
        await upsertTier(weddingId, {
          tier_type: form.tier_type,
          price_cents: priceCents,
          capacity,
          close_date: form.close_date || undefined,
          is_active: form.is_active,
        });
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save tier");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tierId: string) => {
    if (!window.confirm("Delete this ticket tier?")) return;
    setError("");
    try {
      await deleteTier(weddingId, tierId);
      if (form.editingId === tierId) resetForm();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete tier");
    }
  };

  if (loading) return <Loading message="Loading ticket tiers..." />;

  return (
    <div className="tier-setup">
      <h2 className="manage-section-title">Ticket Tiers</h2>
      <p className="form-hint">
        Configure up to four pricing tiers for this wedding. Sold counts update as guests
        purchase seats. Guest-Funded Seats and Extended Guest Seats use your guest list —
        capacity is N/A.
      </p>

      {error && <ErrorMessage message={error} onRetry={load} />}

      {tiers.length === 0 ? (
        <p className="empty-state">No tiers yet. Add your first tier below.</p>
      ) : (
        <div className="guest-table-wrap" style={{ marginBottom: "2rem" }}>
          <table className="guest-table">
            <thead>
              <tr>
                <th>Tier</th>
                <th>Price</th>
                <th>Sold / Capacity</th>
                <th>Close Date</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((tier) => (
                <tr key={tier.id}>
                  <td>
                    <strong>{TICKET_TIER_LABELS[tier.tier_type]}</strong>
                    <p className="form-hint" style={{ margin: "0.25rem 0 0" }}>
                      {TICKET_TIER_DESCRIPTIONS[tier.tier_type]}
                    </p>
                  </td>
                  <td>{formatPrice(tier.price_cents)}</td>
                  <td>{capacityLabel(tier)}</td>
                  <td>
                    {tier.close_date
                      ? new Date(tier.close_date).toLocaleDateString()
                      : "—"}
                  </td>
                  <td>
                    <span
                      className={`status-badge ${
                        tier.is_active ? "status-confirmed" : "status-declined"
                      }`}
                    >
                      {tier.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className="button-row">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => startEdit(tier)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleDelete(tier.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form
        ref={formRef}
        id="tier-setup-form"
        className={`form tier-setup-form${form.editingId ? " is-editing" : ""}`}
        onSubmit={handleSubmit}
      >
        <h4>{form.editingId ? "Update Tier" : "Add Or Edit Tier"}</h4>

        <div className="form-group">
          <label htmlFor="tier_type">Tier Type</label>
          <select
            id="tier_type"
            value={form.tier_type}
            disabled={Boolean(form.editingId)}
            onChange={(e) => {
              const nextType = e.target.value as TicketTier;
              setForm((p) => ({
                ...p,
                tier_type: nextType,
                label: TICKET_TIER_LABELS[nextType],
                capacity: tierTracksCapacity(nextType) ? p.capacity || "10" : p.capacity,
              }));
            }}
          >
            {TIER_TYPES.map((tier) => (
              <option key={tier} value={tier}>
                {TICKET_TIER_LABELS[tier]}
              </option>
            ))}
          </select>
          <p className="form-hint">{TICKET_TIER_DESCRIPTIONS[form.tier_type]}</p>
        </div>

        {form.editingId && (
          <div className="form-group">
            <label htmlFor="tier_label">Name</label>
            <input id="tier_label" type="text" value={form.label} disabled readOnly />
            <p className="form-hint">Name is set by the tier type.</p>
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="tier_price">Price (USD)</label>
            <input
              ref={priceRef}
              id="tier_price"
              type="number"
              min="0"
              step="0.01"
              required
              value={form.price_usd}
              onChange={(e) => setForm((p) => ({ ...p, price_usd: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label htmlFor="tier_capacity">Capacity</label>
            {tracksCapacity ? (
              <input
                id="tier_capacity"
                type="number"
                min="0"
                required
                value={form.capacity}
                onChange={(e) => setForm((p) => ({ ...p, capacity: e.target.value }))}
              />
            ) : (
              <>
                <input id="tier_capacity" type="text" value="N/A" disabled readOnly />
                <p className="form-hint">
                  Capacity is managed by your guest list for this tier.
                </p>
              </>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="tier_close_date">Close Date</label>
          <input
            id="tier_close_date"
            type="date"
            value={form.close_date}
            onChange={(e) => setForm((p) => ({ ...p, close_date: e.target.value }))}
          />
        </div>

        <div className="form-group checkbox-group">
          <label>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
            />
            Active
          </label>
        </div>

        <div className="button-row">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving
              ? "Saving..."
              : form.editingId
                ? "Update Tier"
                : "Save Tier"}
          </button>
          {form.editingId && (
            <button type="button" className="btn btn-secondary" onClick={resetForm}>
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default TierSetup;
